import React, { Fragment, FunctionComponent, useState } from 'react';
import { Icon } from '../Icon';
import cn from 'classnames';
import { useLanguage } from '@/contexts/LanguageContext';
import compact from 'lodash/compact';

export type AccordionProps = {
  items: Array<AccordionItem>;
  titleClassName?: string;
};

type AccordionItem = {
  title?: string | null;
  content?: string | null;
};

export const Accordion: FunctionComponent<AccordionProps> = ({ items, titleClassName }) => {
  const { t } = useLanguage();
  const [openItems, setOpenItems] = useState<string[]>([]);

  const toggle = (title: Exclude<AccordionItem['title'], undefined | null>) => () => {
    if (openItems.includes(title)) {
      setOpenItems(openItems.filter((items) => items !== title));
      return;
    }
    setOpenItems([...openItems, title]);
  };
  const [showOpenAll, setShowOpenAll] = useState(true);
  const openAll = () => {
    setOpenItems(compact(items.map(({ title }) => title)));
    setShowOpenAll(false);
  };

  const closeAll = () => {
    setOpenItems([]);
    setShowOpenAll(true);
  };

  return (
    <div className="flex flex-col w-full">
      {items.map(({ title, content }, index) => {
        if (!content || !title) return null;
        const isOpen = openItems.includes(title);
        return (
          <Fragment key={index}>
            <button
              className={cn('flex items-center justify-between w-full py-2 pr-2 border-gray-700 direction-switch text-left group', {
                'border-b': !isOpen,
              })}
              aria-expanded={isOpen ? 'true' : 'false'}
              aria-controls={title}
              aria-disabled="false"
              onClick={toggle(title)}
            >
              <div className={cn(titleClassName, 'group-hover:font-bold transition-all duration-200')}>{title}</div>

              <div className="group-hover:bg-dark-primary h-8 w-8 grid place-items-center transition-all duration-300 rounded-full">
                <Icon
                  name="Right Arrow"
                  className={cn('group-hover:hidden transform', {
                    'rotate-90': isOpen,
                  })}
                />
                <Icon
                  name="Right Arrow White"
                  className={cn('group-hover:block hidden transform', {
                    'rotate-90': isOpen,
                  })}
                />
              </div>
            </button>
            <div
              className={cn('py-2 border-gray-700 direction-switch htmlcontent', {
                hidden: !isOpen,
                block: isOpen,
                'border-b': isOpen,
              })}
              role="region"
              aria-label={title}
              dangerouslySetInnerHTML={{ __html: content }}
            ></div>
          </Fragment>
        );
      })}
      {showOpenAll ? (
        <button className="self-end mt-3 text-sm underline" onClick={openAll}>
          {t.components.accordion.open_all}
        </button>
      ) : (
        <button className="self-end mt-3 text-sm underline" onClick={closeAll}>
          {t.components.accordion.close_all}
        </button>
      )}
    </div>
  );
};
